import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { Card, Row, Col, Alert, Spinner } from "react-bootstrap";
import { Bar, Pie } from "react-chartjs-2";
import CartContext from "./CartContext";

export default function ProductInventoryDashboard() {
  const { storeId } = useContext(CartContext);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}ProductInventoryDashboard/Summary/${storeId}`
        );
        setSummary(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load inventory dashboard");
      } finally {
        setLoading(false);
      }
    };
    loadSummary();
  }, [storeId]);

  const categoryData = useMemo(
    () => ({
      labels:
        summary?.categoryWise?.map((c) =>
          c.category === "Unknown" ? "Uncategorized" : c.category
        ) || [],
      datasets: [
        {
          label: "Stock Qty",
          data: summary?.categoryWise?.map((c) => c.qty) || [],
          backgroundColor: "rgba(54,162,235,0.6)",
        },
      ],
    }),
    [summary]
  );

  const warehouseData = useMemo(
    () => ({
      labels: summary?.warehouseWise?.map((w) => w.warehouse) || [],
      datasets: [
        {
          data: summary?.warehouseWise?.map((w) => w.qty) || [],
          backgroundColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
          ],
        },
      ],
    }),
    [summary]
  );

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { position: "bottom" },
        tooltip: { mode: "index", intersect: false },
      },
      scales: {
        x: { beginAtZero: true },
        y: { beginAtZero: true },
      },
    }),
    []
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading inventory dashboard...</span>
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!summary || summary.message)
    return <Alert variant="warning">{summary?.message || "No data"}</Alert>;

  return (
    <div className="container mt-1">
      {/* Dashboard Cards */}
      <Row className="mb-3">
        {[
          {
            title: "Total Products",
            value: summary.totalProducts,
            variant: "primary",
          },
          {
            title: "Total Stock Qty",
            value: summary.totalStockQty.toFixed(2),
            variant: "info",
          },
          {
            title: "Total Stock Value",
            value: `₹ ${summary.stockValue.toFixed(0)}`,
            variant: "success",
          },
          {
            title: "Low Stock",
            value: summary.lowStockCount,
            variant: "warning",
          },
          {
            title: "Out of Stock",
            value: summary.outOfStockCount,
            variant: "danger",
          },
        ].map((card, idx) => (
          <Col key={idx} md={2} sm={6} xs={12} className="mb-2">
            <Card
              className={`p-3 shadow-sm text-center bg-${card.variant} text-white`}
            >
              <h6>{card.title}</h6>
              <h4>{card.value}</h4>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts */}
      <Row>
        <Col md={6} xs={12}>
          <Card className="p-3 shadow-sm mb-3">
            <h6>Category-wise Stock</h6>
            <div style={{ height: "300px" }}>
              <Bar data={categoryData} options={chartOptions} />
            </div>
          </Card>
        </Col>
        <Col md={6} xs={12}>
          <Card className="p-3 shadow-sm mb-3">
            <h6>Warehouse-wise Stock</h6>
            <div style={{ height: "300px" }}>
              <Pie data={warehouseData} options={chartOptions} />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
